/**
 * Renderer de `datagrid` para o motor novo, reusando o `DataGridComponent`.
 *
 * **Por que este arquivo está em `builder-screen-beta/` e não em `screen-engine/`**
 *
 * É a regra de dependência que o plano não abre mão: o motor nunca importa o
 * módulo antigo. Se este renderer morasse no `screen-engine`, ele precisaria
 * importar `DataGridComponent` e `BuilderScreenBetaService`, e o motor novo
 * passaria a depender das 60 mil linhas que a Fase 8 vai apagar — o que tornaria
 * a Fase 8 impossível.
 *
 * Invertendo: o lado LEGADO conhece os dois mundos e se registra no motor pelo
 * token `NODE_RENDERERS` (ver `engine-renderers.ts`). O motor segue sem saber que
 * este arquivo existe.
 *
 * `standalone: false` de propósito: fica declarado no `BuilderScreenBetaModule`,
 * de onde o `DataGridComponent` já vem — sem exportá-lo e sem criar módulo novo
 * para um componente que muda de casa na Fase 6, quando este arquivo some.
 */
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  inject
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { FormGroup } from '@angular/forms';

import { ScreenNode } from '../../screen-engine/model/node-props';
import { RenderMode } from '../../screen-engine/render/node-renderer';
import {
  LegacyGridProps,
  nodeToGridProps
} from '../../screen-engine/render/bridge/node-to-grid-props';
import { BuilderScreenBetaService } from '../services/builder-screen.service';
import { LegacyStateShim } from '../../screen-engine/components/legacy-state-shim';
import { translateTAValue } from '../../system-utils/pipes/translateTA';
import { ScreenContext } from '../../screen-engine/render/screen-context';
import { findPanels } from '../../screen-engine/render/detail/detail-panels';

@Component({
  selector: 'tem-data-grid-renderer',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <app-data-grid
      [props]="gridProps"
      [effectiveDataPath]="effectiveDataPath"
      [previewMode]="previewMode"
      (drawerStateChanged)="onDrawerState($event)"
      (flipStateChanged)="onFlipState($event)" />

    <!-- O painel de detalhe, quando a grade o tira do lugar. Ele NAO e movido:
         e desenhado do zero pelo mesmo despachante da tela, ligado ao mesmo
         formulario — entao o valor que a linha escolhida escreveu ja esta la. -->
    <tem-detail-surface
      [panels]="detailPanels"
      [form]="form"
      [mode]="mode"
      [surface]="surfaceKind"
      [open]="detailOpen"
      [rawWidth]="drawerWidth"
      [title]="detailTitle"
      [emptyText]="emptyText"
      (closed)="onDetailClosed()" />
  `,
  providers: [
    {
      // O `data-grid` injeta `BuilderScreenBetaService` e usa DOIS membros dele:
      // `currentState.nodes` e `getNodeIdsUnderPanel`, ambos para achar os
      // painéis de mestre-detalhe. O serviço de verdade tem 3.132 linhas, cinco
      // dependências no construtor e faz injeção circular — instanciá-lo por
      // grade renderizada traria o editor inteiro junto.
      //
      // O cast existe porque o token É a classe. O contrato real está no
      // `LegacyStateShim`, e `legacy-state-shim.spec.ts` fixa quais membros o
      // `data-grid` usa: se aparecer um terceiro, o teste avisa.
      provide: BuilderScreenBetaService,
      useClass: LegacyStateShim as unknown as typeof BuilderScreenBetaService
    }
  ]
})
export class DataGridRenderer implements OnChanges {

  private readonly translate = inject(TranslateService);
  private readonly context = inject(ScreenContext, { optional: true });
  private readonly cdr = inject(ChangeDetectorRef);

  @Input() node!: ScreenNode<'datagrid'>;
  @Input() form: FormGroup | null = null;
  @Input() mode: RenderMode = 'view';

  gridProps: LegacyGridProps = {};
  effectiveDataPath: string | undefined = undefined;

  /** Os paineis que a grade leva para a gaveta ou para a face virada. */
  detailPanels: readonly ScreenNode[] = [];
  detailOpen = false;
  surfaceKind: 'drawer' | 'flip' = 'drawer';
  drawerWidth: number | undefined = undefined;
  detailTitle = '';
  emptyText = '';

  /**
   * Dados falsos SÓ na prévia do editor, nunca em produção.
   *
   * Hoje as grades do editor mostram `fake-data-generator` sem dizer que é falso,
   * o que é a origem de "funcionou no builder, quebrou em produção" (seção 10 do
   * plano). Amarrar ao `mode` é o primeiro passo: em `view` não há caminho pelo
   * qual dado inventado apareça.
   */
  get previewMode(): boolean {
    return this.mode === 'editPreview';
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['node'] === undefined || this.node === undefined) {
      return;
    }

    // Objeto novo a cada mudança do nó, e SÓ nela: o `data-grid` reage por
    // `ngOnChanges`, então recriar as props a cada ciclo de detecção o faria
    // recarregar dados sem parar.
    // O tradutor entra por aqui: `headerKey` pode ser chave de traducao, e o
    // `data-grid` so sabe exibir texto pronto.
    this.gridProps = nodeToGridProps(
      this.node,
      key => translateTAValue(this.translate, key)
    );
    // Sem "Campo do JSON" preenchido, cai no ID do no — que E o nome do
    // controle no formulario, e e a mesma regra do dado de exemplo e da ligacao
    // de dados. Sem ela a grade nao tem onde guardar linha, e o botao Incluir
    // desiste em silencio.
    const caminho = this.node.binding?.dataPath;
    this.effectiveDataPath =
      typeof caminho === 'string' && caminho.length > 0 ? caminho : this.node.id;

    this.surfaceKind = this.node.props.viewMode === 'flip' ? 'flip' : 'drawer';
    this.drawerWidth = this.node.props.drawerWidth;
    this.detailTitle = this.gridProps.label ?? '';
    this.emptyText = translateTAValue(
      this.translate,
      'builderScreen.masterDetail.noFieldsTitle'
    );

    // Trocar de modo com a gaveta aberta a deixaria aberta e vazia.
    this.detailOpen = false;
    this.detailPanels = [];
  }

  /** A grade pediu a gaveta. `panelIds` vem resolvido por ela. */
  onDrawerState(state: { active: boolean; panelIds: string[] }): void {
    this.showDetail('drawer', state.active, state.panelIds);
  }

  /** A grade virou a tela. Ela mesma esconde a face da grade. */
  onFlipState(state: { active: boolean; panelIds: string[] }): void {
    this.showDetail('flip', state.active, state.panelIds);
  }

  /** Fechar pela gaveta. A grade precisa saber, senao ela se acha aberta. */
  onDetailClosed(): void {
    this.detailOpen = false;
    this.cdr.markForCheck();
  }

  private showDetail(
    kind: 'drawer' | 'flip',
    active: boolean,
    panelIds: readonly string[]
  ): void {

    this.surfaceKind = kind;
    this.detailOpen = active;
    this.detailPanels = active
      ? findPanels(this.context?.document() ?? null, panelIds)
      : [];

    // `markForCheck`, e nao `detectChanges`: o evento vem de dentro do
    // `app-data-grid`, ainda no mesmo ciclo, e forcar deteccao aqui rodaria o
    // ciclo duas vezes por clique.
    this.cdr.markForCheck();
  }
}
